import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { LOCALE, ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getItemPrototypeAdaptedDefault } from "~/common/defaults/item-prototype.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { itemPrototypeNormalize } from "~/common/helpers/item-prototype-adapter";
import { serializeItemPrototypeFilters } from "~/common/helpers/serialize-item-prototype-filters";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { CreateItemPrototypeDto } from "~/common/interfaces/dto/item-prototypes/create-item-prototype.dto";
import type { GetAllItemPrototypesDto } from "~/common/interfaces/dto/item-prototypes/get-all-item-prototypes.dto";
import type { UpdateItemPrototypeByIdDto } from "~/common/interfaces/dto/item-prototypes/update-item-prototype-by-id.dto";
import type { ItemPrototype, ItemPrototypeAdapted } from "~/common/interfaces/entities/item-prototype.interface";
import type { Filter } from "~/common/interfaces/filter.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useItemPrototypesStore = defineStore(
	"itemPrototypes",
	() => {
		const { locale } = useI18n();
		const itemPrototypeNormalizeWithLocale = itemPrototypeNormalize.bind(this, locale as Ref<LOCALE>);

		const currentItemPrototype = ref<ItemPrototypeAdapted>(getItemPrototypeAdaptedDefault());
		const itemPrototypes = ref<ItemPrototypeAdapted[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllItemPrototypesDto & PaginationDto>();
		const tableFilter = ref<Filter[]>([
			{
				name: "Status",
				value: {
					active: false,
					inactive: false
				}
			}
		]);
		const tableSort = ref<Sort>({
			columns: [],
			orderBy: ["createdAt", ORDER_BY.DESC]
		});

		const createOne = (
			dto: CreateItemPrototypeDto
		): Promise<IdDto> => {
			return useNuxtApp().$apiFetch<IdDto>(
				"/item-prototypes",
				{
					method: "POST",
					body: dto
				}
			);
		};
		const getAll = async (
			dto: GetAllItemPrototypesDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<ItemPrototypeAdapted>> => {
			const filter = serializeItemPrototypeFilters(tableFilter.value);
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				...filter,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<ItemPrototype>>(
				"/item-prototypes",
				{ query: recentGetAllQuery.value }
			);
			itemPrototypes.value = res.data.map(itemPrototype => itemPrototypeNormalizeWithLocale(itemPrototype)); ;
			meta.value = res.meta;

			return res as unknown as PaginationResponseDto<ItemPrototypeAdapted>;
		};
		const getById = async (id: number): Promise<ItemPrototypeAdapted> => {
			const itemPrototype = await useNuxtApp().$apiFetch<ItemPrototype>(`/item-prototypes/${id}`);

			return itemPrototypeNormalizeWithLocale(itemPrototype);
		};
		const patchById = async (
			id: number,
			dto: UpdateItemPrototypeByIdDto
		): Promise<ItemPrototypeAdapted> => {
			const itemPrototype = await useNuxtApp().$apiFetch<ItemPrototype>(`/item-prototypes/${id}`, {
				method: "PATCH",
				body: dto
			});

			return itemPrototypeNormalizeWithLocale(itemPrototype);
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/item-prototypes/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentItemPrototype,
			itemPrototypes,
			meta,
			recentGetAllQuery,
			tableFilter,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableFilter", "tableSort.orderBy"]
		}
	}
);
